import React from 'react';
import Icon from '@/components/atoms/Icon';
import Badge from '@/components/atoms/Badge/Badge';

interface RecordingRowProps {
  id: string;
  title: string;
  label?: string;
  isOriginal?: boolean;
  isActive: boolean;
  isPublished?: boolean;
  onSelect: () => void;
  onDownload?: () => void;
  onDelete?: () => void;
  isDownloading?: boolean;
  isDeleting?: boolean;
}

function RecordingRow({
  title,
  label,
  isActive,
  isOriginal,
  isPublished,
  onSelect,
  onDownload,
  onDelete,
  isDownloading = false,
  isDeleting = false
}: RecordingRowProps) {
  const [isHovered, setIsHovered] = React.useState(false);
  // The action buttons live inside the clickable row, so stop the click from also selecting the recording.
  const handleAction = (e: React.MouseEvent, action?: () => void) => {
    e.stopPropagation();
    action?.();
  };

  // While either action is in flight both buttons are locked to avoid overlapping requests.
  const actionsDisabled = isDownloading || isDeleting;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group flex h-12 cursor-pointer items-center gap-2 rounded px-3 text-sm text-slate-900 hover:bg-slate-50"
    >
      <span className="shrink-0">
        <Icon name={isOriginal ? 'IconOriginalRecording' : 'IconCopy'} size={20} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium" title={label ?? title}>
          {label ?? title}
        </span>
      </div>
      <div className="flex shrink-0 items-center">
        {isPublished && !actionsDisabled && (
          <div className="flex group-hover:hidden">
            <Badge variation="gray">
              <span className="flex items-center gap-1">
                <Icon name="IconArrowBarToUp" size={12} />
                Published
              </span>
            </Badge>
          </div>
        )}
        <div className={actionsDisabled ? 'flex shrink-0' : 'hidden shrink-0 group-hover:flex'}>
          <button
            onClick={e => handleAction(e, onDownload)}
            disabled={actionsDisabled}
            title="Download"
            className="rounded p-1 text-slate-900 hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Icon
              name={isDownloading ? 'IconLoader2' : 'IconDownload'}
              size={20}
              className={isDownloading ? 'animate-spin' : ''}
            />
          </button>
          {!isOriginal && (
            <button
              onClick={e => handleAction(e, onDelete)}
              disabled={actionsDisabled}
              title="Delete"
              className="rounded p-1 text-slate-900 hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Icon
                name={isDeleting ? 'IconLoader2' : 'IconTrash'}
                size={20}
                className={isDeleting ? 'animate-spin' : ''}
              />
            </button>
          )}
        </div>
      </div>
      {isActive && !isHovered && (
        <span className="flex w-4 shrink-0 items-center justify-center">
          <Icon name="IconCheck" size={20} />
        </span>
      )}
    </div>
  );
}

export default RecordingRow;
